import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiGrid, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { KccLineChart } from "@/components/kcc/Charts";

export default function MetallurgicalAccounting() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/metallurgy/overview").then(({ data }) => setData(data)); }, []);
    const recon = (data?.recon || []).slice(0, 24).slice().reverse().map((r) => ({
        date: r.period.slice(2),
        cu_rec: r.cu_recovery_pct,
        co_rec: r.co_recovery_pct,
        var_cu: r.variance_cu_pct,
        var_co: r.variance_co_pct,
    }));
    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Metallurgical Accounting"
                title="Mine-to-Metal Reconciliation"
                description="Auditable reconciliation across ore mined → plant feed → concentrate → refined product for copper and cobalt, with variance approval and audit trail."
            />
            <KpiGrid kpis={data?.kpis || []} accent="copper" cols={6} />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Panel title="Recovery Trend" subtitle="24 months of Cu & Co plant recovery" accent="copper">
                    <KccLineChart
                        data={recon}
                        series={[
                            { key: "cu_rec", label: "Cu Recovery %", color: "#e88c4a" },
                            { key: "co_rec", label: "Co Recovery %", color: "#4f8bd9" },
                        ]}
                        height={280}
                    />
                </Panel>
                <Panel title="Reconciliation Variance" subtitle="Recovered vs sold · %" accent="cobalt">
                    <KccLineChart
                        data={recon}
                        series={[
                            { key: "var_cu", label: "Cu Variance %", color: "#e88c4a" },
                            { key: "var_co", label: "Co Variance %", color: "#4f8bd9" },
                        ]}
                        height={280}
                    />
                </Panel>
            </div>

            <Panel title="Monthly Metal Balance" subtitle="Ore → Plant → Recovered → Sold · with variance approval" accent="copper">
                <DataTable
                    columns={[
                        { key: "period", label: "Period" },
                        { key: "ore_mined_t", label: "Ore Mined (t)", render: (r) => r.ore_mined_t.toLocaleString() },
                        { key: "cu_grade_pct", label: "Cu %" },
                        { key: "co_grade_pct", label: "Co %" },
                        { key: "cu_recovery_pct", label: "Cu Rec %" },
                        { key: "co_recovery_pct", label: "Co Rec %" },
                        { key: "cu_recovered_t", label: "Cu Rec (t)", render: (r) => r.cu_recovered_t.toLocaleString() },
                        { key: "cu_sold_t", label: "Cu Sold (t)", render: (r) => r.cu_sold_t.toLocaleString() },
                        { key: "variance_cu_pct", label: "Cu Var %", render: (r) => <span className={Math.abs(r.variance_cu_pct) > 2 ? "text-kcc-amber" : "text-kcc-green"}>{r.variance_cu_pct > 0 ? "+" : ""}{r.variance_cu_pct}%</span> },
                        { key: "variance_co_pct", label: "Co Var %", render: (r) => <span className={Math.abs(r.variance_co_pct) > 2.5 ? "text-kcc-amber" : "text-kcc-green"}>{r.variance_co_pct > 0 ? "+" : ""}{r.variance_co_pct}%</span> },
                        { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                        { key: "approver", label: "Approver" },
                    ]}
                    rows={data?.recon || []}
                    testid="metallurgy-recon"
                />
            </Panel>
        </div>
    );
}
